/**
 * API Configuration and Utilities
 */

function normalizeApiBase(rawValue?: string): string {
  const value = rawValue?.trim();
  if (!value) return "";

  const stripTrailingApi = (path: string): string => {
    if (path === "/api") return "";
    return path.replace(/\/api\/?$/, "").replace(/\/+$/, "");
  };

  if (/^https?:\/\//i.test(value)) {
    const url = new URL(value);
    const normalizedPath = stripTrailingApi(url.pathname);
    return `${url.origin}${normalizedPath}`;
  }

  return stripTrailingApi(value);
}

// API callers already include "/api" in most route paths, so this value should
// be the backend origin (or empty for same-origin proxying), not "/api" itself.
export const API_BASE = normalizeApiBase(import.meta.env.VITE_API_URL);
const ACCESS_TOKEN_KEY = "mm_access_token";

/**
 * BigInt-safe JSON stringify - converts BigInt values to strings
 */
export function safeJsonStringify(obj: unknown): string {
  return JSON.stringify(obj, (_key, value) =>
    typeof value === "bigint" ? value.toString() : value,
  );
}

/**
 * Read the persisted access token from localStorage.
 */
export function getAccessToken(): string | null {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem(ACCESS_TOKEN_KEY);
}

/**
 * Fetch wrapper that automatically injects Bearer auth when available.
 */
export async function apiFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const headers = new Headers(init?.headers || {});
  const token = getAccessToken();
  if (token && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  return fetch(input, {
    ...init,
    headers,
  });
}

/**
 * Check API health. Returns null if the backend is unreachable.
 */
export async function checkHealth(): Promise<{
  status: string;
  db: string;
} | null> {
  try {
    const res = await fetch(`${API_BASE}/health`);
    return res.json();
  } catch {
    return null;
  }
}
